// src/shared/hooks/useLocalStorage.js
import { useEffect, useState } from 'react';

/**
 * Um Hook React personalizado que sincroniza o estado com localStorage.
 *
 * Este hook fornece uma maneira de persistir o estado no localStorage do navegador
 * mantendo-o sincronizado com o gerenciamento de estado do React. Ele lida automaticamente
 * com a serialização e deserialização de dados JSON.
 *
 * @param {string} key - A chave do localStorage sob a qual o valor será armazenado
 * @param {any} initial - O valor inicial a ser usado se nenhum valor estiver armazenado
 * @returns {[any, (value: any) => void]} Uma tupla contendo:
 *   - O valor atual (sincronizado com localStorage)
 *   - Uma função setter para atualizar tanto o estado quanto o localStorage
 *
 * @example
 * // Exemplo de uso:
 * const [theme, setTheme] = useLocalStorage('theme', 'light');
 * const [user, setUser] = useLocalStorage('user', { name: '', email: '' });
 *
 * // Atualizar valor
 * setTheme('dark');
 * setUser({ name: 'John', email: 'john@example.com' });
 */
export function useLocalStorage(key, initial) {
    const [value, setValue] = useState(() => {
        try { return JSON.parse(localStorage.getItem(key)) ?? initial; } catch { return initial; }
    });
    useEffect(() => {
        if (value == null) {
            localStorage.removeItem(key);
            return;
        }
        localStorage.setItem(key, JSON.stringify(value));
    }, [key, value]);
    return [value, setValue];
}
